import { supabase } from "./supabase";
import * as Sentry from "@sentry/react";

const API_BASE = import.meta.env.VITE_API_URL || "https://api.careersorter.co";
const API_URL = `${API_BASE}/v1`;

async function refreshToken() {
  const { data, error } = await supabase.auth.refreshSession();
  if (error) throw error;
  return data.session?.access_token;
}

async function fetchWithRetry(url, options, token) {
  const headers = { ...options.headers };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let res = await fetch(url, { ...options, headers });

  if (res.status === 401) {
    try {
      const newToken = await refreshToken();
      if (newToken) {
        headers["Authorization"] = `Bearer ${newToken}`;
        res = await fetch(url, { ...options, headers });
      }
    } catch {
      // refresh failed, return the 401 response
    }
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const error = new Error(`API ${res.status}: ${text}`);
    Sentry.captureException(error, { extra: { url, status: res.status } });
    throw error;
  }
  return res.json();
}

export async function apiGet(path, token) {
  return fetchWithRetry(`${API_URL}${path}`, {
    headers: { "Content-Type": "application/json" },
  }, token);
}

export async function apiPost(path, body, token) {
  return fetchWithRetry(`${API_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  }, token);
}

export async function apiDelete(path, token) {
  return fetchWithRetry(`${API_URL}${path}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  }, token);
}

export function apiUpload(path, formData, token, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_URL}${path}`);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(e.loaded / e.total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          resolve({ ok: true });
        }
      } else {
        const error = new Error(`Upload failed: ${xhr.status}`);
        Sentry.captureException(error, { extra: { url: `${API_URL}${path}`, status: xhr.status } });
        reject(error);
      }
    };

    xhr.onerror = () => {
      const error = new Error("Upload network error");
      Sentry.captureException(error, { extra: { url: `${API_URL}${path}` } });
      reject(error);
    };
    xhr.send(formData);
  });
}
